import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import DashboardView from "../views/Dashboard.vue";
import { ifAuthDeny } from "./guards/auth";
import { ifNotAuthDeny } from "./guards/not-auth";
import { ifNotAuthOnlyDeny } from "./guards/auth-only";
import store from "@/store";
import { BOOTSTRAP_IMPERSONATION } from "@/store/action-types";
import { parseImpersonationToken } from "@/utils/parseImpersonationToken";
import {
  ACTIVITY_REPORTS_ROUTE,
  AI_AGENT_ROUTE,
  BUCKETS_LIST_ROUTE,
  BUCKETS_ROUTE,
  CALL_HISTORY_REPORTS_ROUTE,
  DIALER_SESSION_ROUTE,
  DIALER_SESSION_HISTORY_ROUTE,
  DIALER_SESSION_DETAIL_ROUTE,
  COMPANIES_ROUTE,
  COMPANY_OVERVIEW_ROUTE,
  CONTACT_IMPORT_ROUTE,
  CONTACT_OVERVIEW_ROUTE,
  CONTACTS_ROUTE,
  CONVERT_CONTACT_OPPORTUNITY_ROUTE,
  DASHBOARD_ROUTE,
  EMAIL_VERIFICATION_WALL_ROUTE,
  ONBOARDING_ROUTE,
  ERROR_ROUTE,
  FORGOT_PASSWORD_ROUTE,
  LOGIN_ROUTE,
  MFA_VERIFY_ROUTE,
  MEETINGS_ROUTE,
  OAUTH_AUTHORIZE_ROUTE,
  OPPORTUNITIES_REPORTS_ROUTE,
  OPPORTUNITIES_ROUTE,
  OPPORTUNITY_OVERVIEW_ROUTE,
  PRIVACY_POLICY_ROUTE,
  TERMS_OF_USE_ROUTE,
  REPORTS_ROUTE,
  RESET_PASSWORD_ROUTE,
  SETTINGS_ADMIN_ROUTE,
  SETTINGS_AUTO_BUCKET_ROUTE,
  SETTINGS_BILLING_ROUTE,
  SETTINGS_BUY_NEW_NUMBER_ROUTE,
  SETTINGS_CALL_PHONE_NUMBERS_ROUTE,
  SETTINGS_CALL_RECORDINGS_ROUTE,
  SETTINGS_DOWNLOADS_ROUTE,
  SETTINGS_GENERAL_ROUTE,
  SETTINGS_INTEGRATIONS_ROUTE,
  SETTINGS_PROFILE_ROUTE,
  SETTINGS_ROLE_DETAIL_ROUTE,
  SIGNUP_ROUTE,
  PAYMENT_SETUP_SUCCESS_ROUTE,
  SUBSCRIPTION_CANCELLED_ROUTE,
  SUBSCRIPTION_SUCCESS_ROUTE,
  SUBSCRIPTION_WALL_ROUTE,
  SUPPORT_ROUTE,
  TEMPLATE_FOLDERS_ROUTE,
  TEMPLATES_ROUTE,
  SEQUENCES_ROUTE,
  SEQUENCE_CREATE_ROUTE,
  SEQUENCE_DETAIL_ROUTE,
  UNIPILE_ERROR_ROUTE,
  UNIPILE_SUCCESS_ROUTE,
  VERIFY_EMAIL_ROUTE,
} from "./routes";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: DASHBOARD_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: DashboardView,
    meta: { title: "Dashboard" },
  },
  {
    path: "/getting-started",
    name: ONBOARDING_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/onboarding/GettingStarted.vue"),
    meta: { title: "Getting Started" },
  },
  {
    path: "/login",
    name: LOGIN_ROUTE,
    beforeEnter: ifAuthDeny,
    component: () => import("../views/auth/Login.vue"),
    meta: { title: "Login" },
  },
  {
    path: "/signup",
    name: SIGNUP_ROUTE,
    beforeEnter: ifAuthDeny,
    component: () => import("../views/auth/Signup.vue"),
    meta: { title: "Signup" },
  },
  {
    path: "/forgot-password",
    name: FORGOT_PASSWORD_ROUTE,
    beforeEnter: ifAuthDeny,
    component: () => import("../views/auth/ForgotPassword.vue"),
    meta: { title: "Forgot Password" },
  },
  {
    path: "/mfa/verify",
    name: MFA_VERIFY_ROUTE,
    beforeEnter: ifAuthDeny,
    component: () => import("../views/auth/VerifyMfa.vue"),
    meta: { title: "Verify Your Identity" },
  },
  {
    path: "/reset-password/:token",
    name: RESET_PASSWORD_ROUTE,
    beforeEnter: ifAuthDeny,
    component: () => import("../views/auth/ResetPassword.vue"),
    meta: { title: "Reset Password" },
  },
  {
    path: "/staff/set-password/:token",
    component: () => import("../views/auth/ResetPassword.vue"),
    meta: { title: "Set Password" },
  },
  {
    path: "/verify-email/:id",
    name: VERIFY_EMAIL_ROUTE,
    // beforeEnter: ifAuthDeny,
    component: () => import("@/views/auth/VerifyEmail.vue"),
    meta: { title: "Email Verification Wall" },
  },
  {
    path: "/email-verification-wall",
    name: EMAIL_VERIFICATION_WALL_ROUTE,
    // beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/auth/EmailVerificationWall.vue"),
    meta: { title: "Email Verification Wall" },
  },
  {
    path: "/privacy-policy",
    name: PRIVACY_POLICY_ROUTE,
    component: () => import("@/views/legal/PrivacyPolicy.vue"),
    meta: { title: "Privacy Policy" },
  },
  {
    path: "/terms-of-use",
    name: TERMS_OF_USE_ROUTE,
    component: () => import("@/views/legal/TermsOfUse.vue"),
    meta: { title: "Terms of Use" },
  },
  {
    path: "/contacts",
    name: CONTACTS_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/contacts/Contacts.vue"),
    meta: { title: "Contacts" },
  },
  {
    path: "/contacts/:id",
    name: CONTACT_OVERVIEW_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/contacts/ContactOverview.vue"),
    meta: { title: "Contact Overview" },
  },
  {
    path: "/contacts/:id/convert-opportunity",
    name: CONVERT_CONTACT_OPPORTUNITY_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/contacts/ConvertOpportunity.vue"),
    meta: { title: "Convert Opportunity" },
  },
  {
    path: "/contacts/import",
    name: CONTACT_IMPORT_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/contacts/ImportContact.vue"),
    meta: { title: "Import Contacts" },
  },
  {
    path: "/accounts",
    name: COMPANIES_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/company/Companies.vue"),
    meta: { title: "Accounts" },
  },
  {
    path: "/accounts/:id",
    name: COMPANY_OVERVIEW_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/company/CompanyOverview.vue"),
    meta: { title: "Company Overview" },
  },
  {
    path: "/meetings",
    name: MEETINGS_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/meeting/Meetings.vue"),
    meta: { title: "Meetings" },
  },
  {
    path: "/buckets",
    name: BUCKETS_LIST_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/bucket/BucketsList.vue"),
    meta: { title: "Buckets" },
  },
  {
    path: "/buckets/:bucket",
    name: BUCKETS_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/bucket/Buckets.vue"),
    meta: { title: "Buckets" },
  },
  {
    path: "/ai-agent",
    name: AI_AGENT_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/agent/AgentView.vue"),
    meta: { title: "AI Agent" },
  },
  {
    path: "/dialer",
    name: DIALER_SESSION_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/dialer/DialerSession.vue"),
    meta: { title: "Power Dialer" },
  },
  {
    path: "/dialer/history",
    name: DIALER_SESSION_HISTORY_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/dialer/DialerSessionHistory.vue"),
    meta: { title: "Dialer Session History" },
  },
  {
    path: "/dialer/history/:id",
    name: DIALER_SESSION_DETAIL_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/dialer/DialerSessionDetail.vue"),
    meta: { title: "Dialer Session Detail" },
  },
  {
    path: "/opportunity",
    name: OPPORTUNITIES_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/opportunity/Opportunities.vue"),
    meta: { title: "Opportunities" },
  },
  {
    path: "/opportunity/:id/overview",
    name: OPPORTUNITY_OVERVIEW_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/opportunity/OpportunityOverview.vue"),
    meta: { title: "Opportunity Overview" },
  },
  {
    path: "/template-folders",
    name: TEMPLATE_FOLDERS_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/template/TemplateFolders.vue"),
    meta: { title: "Template Folders" },
  },
  {
    path: "/templates/:folderId",
    name: TEMPLATES_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/template/Templates.vue"),
    meta: { title: "Templates" },
  },
  {
    path: "/sequences",
    name: SEQUENCES_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/sequence/Sequences.vue"),
    meta: { title: "Sequences" },
  },
  {
    path: "/sequences/new",
    name: SEQUENCE_CREATE_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/sequence/CreateSequence.vue"),
    meta: { title: "Create Sequence" },
  },
  {
    path: "/sequences/:id",
    name: SEQUENCE_DETAIL_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/sequence/SequenceDetail.vue"),
    meta: { title: "Sequence Detail" },
  },
  {
    path: "/report/activity",
    name: ACTIVITY_REPORTS_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/reports/ActivityReport.vue"),
    meta: { title: "Activity Reports" },
  },
  {
    path: "/reports",
    name: REPORTS_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/reports/Report.vue"),
    meta: { title: "Reports" },
  },
  {
    path: "/call-history",
    name: CALL_HISTORY_REPORTS_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/call-history/CallHistory.vue"),
    meta: { title: "Call History" },
  },
  {
    path: "/reports/opportunities",
    name: OPPORTUNITIES_REPORTS_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/reports/OpportunitiesReport.vue"),
    meta: { title: "Opportunities Report" },
  },
  {
    path: "/subscription-wall",
    name: SUBSCRIPTION_WALL_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/subscription/SubscriptionWall.vue"),
    meta: { title: "Subscription Wall" },
  },
  {
    path: "/subscription-success/:id",
    name: SUBSCRIPTION_SUCCESS_ROUTE,
    component: () => import("@/views/subscription/SubscriptionSuccess.vue"),
    meta: { title: "Subscription Success" },
  },
  {
    path: "/subscription-cancelled",
    name: SUBSCRIPTION_CANCELLED_ROUTE,
    component: () => import("@/views/subscription/SubscriptionCancelled.vue"),
    meta: { title: "Subscription Cancelled" },
  },
  {
    path: "/payment-setup-success",
    name: PAYMENT_SETUP_SUCCESS_ROUTE,
    component: () => import("@/views/subscription/PaymentSetupSuccess.vue"),
    meta: { title: "Payment Setup Success" },
  },
  {
    path: "/error",
    name: ERROR_ROUTE,
    component: () => import("@/views/Error.vue"),
    meta: { title: "Error" },
  },
  {
    path: "/settings",
    redirect: { name: SETTINGS_PROFILE_ROUTE },
  },
  {
    path: "/settings/general",
    name: SETTINGS_GENERAL_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/settings/GeneralSettings.vue"),
    meta: { title: "Settings" },
  },
  {
    path: "/settings/profile",
    name: SETTINGS_PROFILE_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/settings/ProfileSettings.vue"),
    meta: { title: "Settings" },
  },
  {
    path: "/settings/admin",
    name: SETTINGS_ADMIN_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/settings/AdminSettings.vue"),
    meta: { title: "Settings" },
  },
  {
    path: "/settings/billing",
    name: SETTINGS_BILLING_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/settings/BillingSettings.vue"),
    meta: { title: "Billing & Usage" },
  },
  {
    path: "/settings/integrations",
    name: SETTINGS_INTEGRATIONS_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/settings/IntegrationSettings.vue"),
    meta: { title: "Settings" },
  },
  {
    path: "/settings/downloads",
    name: SETTINGS_DOWNLOADS_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/settings/Downloads.vue"),
    meta: { title: "Settings" },
  },
  {
    path: "/settings/call-audio",
    redirect: { name: SETTINGS_CALL_PHONE_NUMBERS_ROUTE },
  },
  {
    path: "/settings/call-phone-numbers",
    name: SETTINGS_CALL_PHONE_NUMBERS_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/settings/PhoneNumbers.vue"),
    meta: { title: "Settings" },
  },
  {
    path: "/settings/call-phone-numbers/buy-number",
    name: SETTINGS_BUY_NEW_NUMBER_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/settings/BuyNumber.vue"),
    meta: { title: "Settings" },
  },
  {
    path: "/settings/call-recordings",
    name: SETTINGS_CALL_RECORDINGS_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/settings/CallRecordings.vue"),
    meta: { title: "Settings" },
  },
  {
    path: "/settings/admin/roles/:roleName",
    name: SETTINGS_ROLE_DETAIL_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/settings/RoleDetail.vue"),
    meta: { title: "Role Settings" },
  },
  {
    path: "/settings/auto-bucket",
    name: SETTINGS_AUTO_BUCKET_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/settings/AutoBucketSettings.vue"),
    meta: { requiresAuth: true, title: "Settings" },
  },
  {
    path: "/support",
    name: SUPPORT_ROUTE,
    beforeEnter: ifNotAuthOnlyDeny,
    component: () => import("@/views/support/Support.vue"),
    meta: { title: "Support Center" },
  },
  {
    path: "/unipile/success",
    name: UNIPILE_SUCCESS_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/unipile/UnipileSuccess.vue"),
    meta: { title: "Account Connected" },
  },
  {
    path: "/unipile/error",
    name: UNIPILE_ERROR_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import("@/views/unipile/UnipileError.vue"),
    meta: { title: "Connection Error" },
  },
  {
    path: "/oauth/authorize",
    name: OAUTH_AUTHORIZE_ROUTE,
    component: () => import("@/views/oauth/OAuthAuthorize.vue"),
    meta: { title: "Authorization" },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Bootstrap impersonation session when the admin opens the app with ?token=
router.beforeEach(async (to, _from, next) => {
  const token = to.query.token as string | undefined;
  if (token && !store.getters.isImpersonating) {
    const payload = parseImpersonationToken(token);
    if (payload?.impersonatedBy) {
      try {
        await store.dispatch(BOOTSTRAP_IMPERSONATION, token);
        // Strip the token from the URL so it isn't bookmarked or logged
        const cleanQuery = { ...to.query };
        delete cleanQuery.token;
        return next({ ...to, query: cleanQuery, replace: true });
      } catch {
        return next({ name: LOGIN_ROUTE });
      }
    }
  }
  return next();
});

router.afterEach((to) => {
  const title = to.meta.title as string;
  if (title) {
    document.title = title;
  }
});

router.onError((error, to) => {
  const isChunkError =
    error.name === "ChunkLoadError" ||
    /Loading chunk \d+ failed/.test(error.message) ||
    /Failed to fetch dynamically imported module/.test(error.message);

  if (isChunkError) {
    window.location.href = to?.fullPath ?? "/";
  }
});

export default router;
